import React from 'react';
import { Box, Container, Grid, Typography, Card, CardContent } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
    AccountBalanceWallet, 
    Visibility, 
    Loyalty, 
    Redeem,
    AdsClick,
    FactCheck,
    BarChart,
    PriceCheck
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const viewerBenefits = [
    {
        icon: <AccountBalanceWallet sx={{ fontSize: 40, color: '#00F0FF' }} />,
        title: 'Connect Wallet',
        description: 'Link your crypto wallet securely to start tracking your rewards.'
    },
    {
        icon: <Visibility sx={{ fontSize: 40, color: '#00F0FF' }} />,
        title: 'Watch Ads',
        description: 'View targeted ads from premium brands and collect points.'
    },
    {
        icon: <Loyalty sx={{ fontSize: 40, color: '#00F0FF' }} />,
        title: 'Collect Points',
        description: 'Accumulate points for every ad interaction you complete.'
    },
    {
        icon: <Redeem sx={{ fontSize: 40, color: '#00F0FF' }} />,
        title: 'Redeem Tokens',
        description: 'Redeem tokens according to the current market price instantly.'
    }
];

const advertiserBenefits = [
    {
        icon: <AdsClick sx={{ fontSize: 40, color: '#00E676' }} />,
        title: 'Targeted Reach',
        description: 'Connect with a highly engaged Web3 audience that matches your project profile.'
    },
    {
        icon: <FactCheck sx={{ fontSize: 40, color: '#00E676' }} />,
        title: 'Verified Results',
        description: 'Blockchain-verified interactions ensure your marketing budget is spent effectively.'
    },
    {
        icon: <BarChart sx={{ fontSize: 40, color: '#00E676' }} />,
        title: 'Real-time Data',
        description: 'Access transparent analytics and insights into your campaign performance.'
    },
    {
        icon: <PriceCheck sx={{ fontSize: 40, color: '#00E676' }} />,
        title: 'Cost Effective',
        description: 'Maximize your ROI with efficient ad delivery and competitive pricing.'
    }
];

const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: {
            staggerChildren: 0.2
        }
    }
};

const item = {
    hidden: { opacity: 0, y: 50 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 50 } }
};

const BenefitsSection = () => {
    const theme = useTheme();

    return (
        <Box id="benefits" sx={{ py: 10 }}>
            <Container maxWidth="lg">
                {/* Viewers Heading */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h2"
                        align="center"
                        sx={{
                            mb: 8,
                            fontWeight: 800,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Benefits for <span style={{ color: '#00F0FF' }}>Viewers</span>
                    </Typography>
                </motion.div>

                {/* Viewers Benefits Grid */}
                <Grid
                    container
                    spacing={3}
                    component={motion.div}
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    sx={{ mb: 12 }}
                >
                    {viewerBenefits.map((benefit, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index} component={motion.div} variants={item}>
                            <Card
                                sx={{
                                    height: '100%',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.8)',
                                    backgroundImage: theme.palette.mode === 'dark' ? 'linear-gradient(145deg, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0) 100%)' : 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(240,240,240,0.8) 100%)',
                                    backdropFilter: 'blur(20px)',
                                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
                                    borderRadius: '24px',
                                    transition: 'all 0.3s ease-in-out',
                                    overflow: 'visible',
                                    '&:hover': {
                                        transform: 'translateY(-10px)',
                                        border: `1px solid #00F0FF`,
                                        boxShadow: `0 10px 40px rgba(0, 240, 255, 0.2)`,
                                        background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.95)',
                                    }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            mb: 3,
                                            p: 2,
                                            borderRadius: '50%',
                                            background: 'rgba(0, 240, 255, 0.1)',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {benefit.icon}
                                    </Box>
                                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 700 }}>
                                        {benefit.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                                        {benefit.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Advertisers Heading */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Typography
                        variant="h2"
                        align="center"
                        sx={{
                            mb: 8,
                            fontWeight: 800,
                            color: theme.palette.text.primary,
                        }}
                    >
                        Benefits for <span style={{ color: '#00E676' }}>Advertisers</span>
                    </Typography>
                </motion.div>

                {/* Advertisers Benefits Grid */}
                <Grid
                    container
                    spacing={3}
                    component={motion.div}
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                >
                    {advertiserBenefits.map((benefit, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index} component={motion.div} variants={item}>
                            <Card
                                sx={{
                                    height: '100%',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.8)',
                                    backgroundImage: theme.palette.mode === 'dark' ? 'linear-gradient(145deg, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0) 100%)' : 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(240,240,240,0.8) 100%)',
                                    backdropFilter: 'blur(20px)',
                                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(0, 0, 0, 0.08)',
                                    borderRadius: '24px',
                                    transition: 'all 0.3s ease-in-out',
                                    overflow: 'visible',
                                    '&:hover': {
                                        transform: 'translateY(-10px)',
                                        border: `1px solid #00E676`,
                                        boxShadow: `0 10px 40px rgba(0, 230, 118, 0.2)`,
                                        background: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.95)',
                                    }
                                }}
                            >
                                <CardContent sx={{ textAlign: 'center', p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                    <Box
                                        sx={{
                                            mb: 3,
                                            p: 2,
                                            borderRadius: '50%',
                                            background: 'rgba(0, 230, 118, 0.1)',
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }}
                                    >
                                        {benefit.icon}
                                    </Box>
                                    <Typography variant="h5" sx={{ mb: 2, color: theme.palette.text.primary, fontWeight: 700 }}>
                                        {benefit.title}
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary, lineHeight: 1.6 }}>
                                        {benefit.description}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default BenefitsSection;
